// Living Atlas source & license matrix. Machine-readable twin of
// docs/living-atlas-sources.md: the Worker reports health against these
// entries and the admin page shows them. "verified" means the endpoint was
// exercised and its terms read on `verifiedOn`; it is not legal advice.

import type { AtlasModeId, LicenseStatus } from "./types";

export interface AtlasProviderManifest {
  id: string;
  name: string;
  modes: AtlasModeId[];
  docsUrl: string;
  termsUrl: string;
  endpoint: string;
  auth: string;
  cadence: string;
  caching: string;
  redistribution: string;
  attribution: string;
  licenseStatus: LicenseStatus;
  supportsCommercialUse: boolean | "unknown";
  /** Built and fetching, or listed only. */
  built: boolean;
  /** `source_health` / `api_usage` key, when built. */
  healthKey?: string;
  verifiedOn: string | null;
}

export const ATLAS_PROVIDERS: AtlasProviderManifest[] = [
  {
    id: "usgs-quakes",
    name: "USGS Earthquake Hazards real-time GeoJSON summary feeds",
    modes: ["earthpulse"],
    docsUrl: "https://earthquake.usgs.gov/earthquakes/feed/v1.0/geojson.php",
    termsUrl: "https://www.usgs.gov/information-policies-and-instructions/copyrights-and-credits",
    endpoint: "https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/{all_hour|all_day|2.5_week|4.5_month}.geojson",
    auth: "None",
    cadence: "Feeds regenerate every minute; Worker caches 2–15 min by window",
    caching: "Allowed (USGS asks automated clients to use these feeds rather than query the FDSN service)",
    redistribution: "Public domain (U.S. Government work); credit requested",
    attribution: "U.S. Geological Survey",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "usgs-quakes",
    verifiedOn: "2026-10-23",
  },
  {
    id: "noaa-swpc-ovation",
    name: "NOAA SWPC OVATION aurora nowcast (ovation_aurora_latest.json)",
    modes: ["aurora-live"],
    docsUrl: "https://www.swpc.noaa.gov/products/aurora-30-minute-forecast",
    termsUrl: "https://www.weather.gov/disclaimer",
    endpoint: "https://services.swpc.noaa.gov/json/ovation_aurora_latest.json",
    auth: "None",
    cadence: "Model runs about every 5 min; Worker caches 10 min",
    caching: "Allowed",
    redistribution: "Public domain (U.S. Government work)",
    attribution: "NOAA Space Weather Prediction Center",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "noaa-swpc-ovation",
    verifiedOn: "2026-10-23",
  },
  {
    id: "noaa-swpc",
    name: "NOAA SWPC planetary K-index (existing Kinmantle provider)",
    modes: ["aurora-live"],
    docsUrl: "https://services.swpc.noaa.gov/products/",
    termsUrl: "https://www.weather.gov/disclaimer",
    endpoint: "https://services.swpc.noaa.gov/products/noaa-planetary-k-index-forecast.json",
    auth: "None",
    cadence: "Worker caches 30 min (shared with the Sky widgets)",
    caching: "Allowed",
    redistribution: "Public domain (U.S. Government work)",
    attribution: "NOAA SWPC",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "noaa-swpc",
    verifiedOn: "2026-10-22",
  },
  {
    id: "nasa-gibs",
    name: "NASA GIBS WMTS (GOES ABI, VIIRS Black Marble, Blue Marble, GHRSST MUR sea-surface temperature)",
    modes: ["live-earth", "earth-at-night", "ocean-alive"],
    docsUrl: "https://nasa-gibs.github.io/gibs-api-docs/access-basics/",
    termsUrl: "https://www.earthdata.nasa.gov/engage/open-data-services-software-policies",
    endpoint: "https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/{layer}/default/{time}/{matrixSet}/{z}/{y}/{x}.{ext}",
    auth: "None",
    cadence: "GOES frames every 10 min, ~30–60 min latency; Worker caches the time list 5 min",
    caching: "Tiles served to browsers directly from NASA's public CDN (CORS *); time domains via Worker",
    redistribution: "NASA open data policy — no restrictions; acknowledgement requested",
    attribution: "Imagery: NASA GIBS / NOAA GOES",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "nasa-gibs",
    verifiedOn: "2026-10-23",
  },
  {
    id: "noaa-radar",
    name: "NOAA MRMS radar via NWS OpenGeo (existing Kinmantle provider)",
    modes: ["live-earth"],
    docsUrl: "https://opengeo.ncep.noaa.gov/geoserver/conus/conus_bref_qcd/ows",
    termsUrl: "https://www.weather.gov/disclaimer",
    endpoint: "/api/radar/timeline (unchanged); latest frame drawn as WMS tiles",
    auth: "None",
    cadence: "Reuses the Weather screen's timeline query (5 min)",
    caching: "Same as the Weather screen",
    redistribution: "Public domain",
    attribution: "Radar: NOAA/NWS MRMS",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "noaa-radar",
    verifiedOn: "2026-10-20",
  },
  {
    id: "nasa-firms",
    name: "NASA FIRMS active fire data (VIIRS NOAA-20 / NOAA-21 / Suomi NPP)",
    modes: ["firewatch"],
    docsUrl: "https://firms.modaps.eosdis.nasa.gov/active_fire/",
    termsUrl: "https://www.earthdata.nasa.gov/data/tools/firms/faq",
    endpoint: "https://firms.modaps.eosdis.nasa.gov/data/active_fire/{sat}/csv/{SAT}_VIIRS_C2_USA_contiguous_and_Hawaii_{24h|48h}.csv; with FIRMS_MAP_KEY: /api/area/csv/{key}/{source}/{bbox}/{days}",
    auth: "None for the U.S. files; free MAP_KEY (publisher Worker secret) for the area API",
    cadence: "Files refresh as overpasses are processed (~hourly); Worker caches 20 min",
    caching: "Allowed; cite LANCE FIRMS and link its disclaimer",
    redistribution: "NASA open data policy; FIRMS asks for citation",
    attribution: "NASA FIRMS (LANCE)",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "nasa-firms",
    verifiedOn: "2026-10-23",
  },
  {
    id: "nifc-wfigs",
    name: "NIFC WFIGS Incident Locations — Current (ArcGIS Feature Service)",
    modes: ["firewatch"],
    docsUrl: "https://data-nifc.opendata.arcgis.com/",
    termsUrl: "https://data-nifc.opendata.arcgis.com/",
    endpoint: "https://services3.arcgis.com/T4QMspbfLg3qTGWY/arcgis/rest/services/WFIGS_Incident_Locations_Current/FeatureServer/0/query",
    auth: "None",
    cadence: "Updated every few minutes by the interagency system; Worker caches 15 min",
    caching: "Allowed for display",
    redistribution: "Interagency government data; no license text published with the service — treat as review-required",
    attribution: "National Interagency Fire Center (WFIGS)",
    licenseStatus: "review-required",
    supportsCommercialUse: "unknown",
    built: true,
    healthKey: "nifc-wfigs",
    verifiedOn: "2026-10-23",
  },
  {
    id: "openfreemap",
    name: "OpenFreeMap vector tiles (OpenStreetMap data, OpenMapTiles schema)",
    modes: [],
    docsUrl: "https://openfreemap.org/",
    termsUrl: "https://openfreemap.org/",
    endpoint: "https://tiles.openfreemap.org/planet (TileJSON) + /fonts",
    auth: "None",
    cadence: "Weekly planet builds; browser fetches visible tiles only",
    caching: "Browser cache only; bulk download limited (3,000 tiles / 50 MB)",
    redistribution: "OSM data ODbL; commercial use allowed by OpenFreeMap; attribution required",
    attribution: "© OpenMapTiles © OpenStreetMap contributors · OpenFreeMap",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    verifiedOn: "2026-10-23",
  },
  {
    id: "usgs-water",
    name: "USGS Water Data OGC APIs (latest-continuous, latest-daily, monitoring-locations)",
    modes: ["riverpulse"],
    docsUrl: "https://api.waterdata.usgs.gov/docs/ogcapi/",
    termsUrl: "https://www.usgs.gov/information-policies-and-instructions/copyrights-and-credits",
    endpoint: "https://api.waterdata.usgs.gov/ogcapi/v0/collections/{latest-continuous,latest-daily,monitoring-locations}/items?bbox=…&parameter_code=00060",
    auth: "None (an optional api.data.gov key raises rate limits)",
    cadence: "15-min values; Worker caches latest 15 min, daily means 3 h, names 7 days",
    caching: "Allowed",
    redistribution: "Public domain (U.S. Government work); provisional data must be labelled",
    attribution: "U.S. Geological Survey",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "usgs-water",
    verifiedOn: "2026-10-23",
  },
  {
    id: "co-dwr",
    name: "Colorado Division of Water Resources — CDSS REST telemetry (reservoir storage)",
    modes: ["riverpulse"],
    docsUrl: "https://dwr.state.co.us/Rest/GET/Help",
    termsUrl: "https://dwr.state.co.us/Rest/GET/Help",
    endpoint: "https://dwr.state.co.us/Rest/GET/api/v2/telemetrystations/{telemetrystation,telemetrytimeseriesday}/?stationType=Storage%20Structure…",
    auth: "None up to 1,000 calls/day; optional free API key (DWR_API_KEY) raises it",
    cadence: "Telemetry every 15 min–1 h; Worker caches 1 h (latest storage + a week of daily values)",
    caching: "Allowed",
    redistribution: "Public Colorado state data, but no license text is published with the service; readings from cooperators (Reclamation, Corps of Engineers, Northern Water…) are credited per station",
    attribution: "Colorado Division of Water Resources",
    licenseStatus: "review-required",
    supportsCommercialUse: "unknown",
    built: true,
    healthKey: "co-dwr",
    verifiedOn: "2026-10-27",
  },
  {
    id: "usgs-nhd-tiles",
    name: "USGS The National Map — National Hydrography Dataset cached overlay (USGSHydroCached)",
    modes: ["riverpulse"],
    docsUrl: "https://basemap.nationalmap.gov/arcgis/rest/services/USGSHydroCached/MapServer",
    termsUrl: "https://www.usgs.gov/information-policies-and-instructions/copyrights-and-credits",
    endpoint: "https://basemap.nationalmap.gov/arcgis/rest/services/USGSHydroCached/MapServer/tile/{z}/{y}/{x}",
    auth: "None",
    cadence: "NHD refreshed periodically; browser fetches visible tiles only",
    caching: "Browser cache only",
    redistribution: "Public domain (U.S. Government work)",
    attribution: "USGS National Hydrography Dataset",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    verifiedOn: "2026-10-27",
  },
  {
    id: "nrcs-awdb",
    name: "USDA NRCS AWDB REST service (SNOTEL)",
    modes: ["mountain-conditions"],
    docsUrl: "https://wcc.sc.egov.usda.gov/awdbRestApi/swagger-ui/index.html",
    termsUrl: "https://wcc.sc.egov.usda.gov/awdbRestApi/swagger-ui/index.html",
    endpoint: "https://wcc.sc.egov.usda.gov/awdbRestApi/services/v1/{stations,data}?stationTriplets=*:{ST}:SNTL&elements=WTEQ,SNWD&centralTendencyType=MEDIAN",
    auth: "None",
    cadence: "Daily values; Worker caches data 1 h, station list 7 days (the service is slow: ~15 s for a state)",
    caching: "Allowed",
    redistribution: "Public domain (U.S. Government work)",
    attribution: "USDA NRCS SNOTEL",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "nrcs-awdb",
    verifiedOn: "2026-10-23",
  },
  {
    id: "epa-airnow-gis",
    name: "EPA AirNow current monitor data (public ArcGIS feature service)",
    modes: ["invisible-air"],
    docsUrl: "https://services.arcgis.com/cJ9YHowT8TU7DUyn/arcgis/rest/services/Air%20Now%20Current%20Monitor%20Data%20Public/FeatureServer/0",
    termsUrl: "https://www.airnow.gov/about-the-data/",
    endpoint: "…/Air Now Current Monitor Data Public/FeatureServer/0/query (active sites, North America envelope, GeoJSON)",
    auth: "None",
    cadence: "Hourly; Worker caches 20 min",
    caching: "Allowed for display",
    redistribution: "EPA data; AirNow asks that it be credited and described as preliminary. No license text on the service itself — review before commercial use",
    attribution: "U.S. EPA AirNow",
    licenseStatus: "review-required",
    supportsCommercialUse: "unknown",
    built: true,
    healthKey: "epa-airnow-gis",
    verifiedOn: "2026-10-23",
  },
  {
    id: "noaa-hms",
    name: "NOAA NESDIS Hazard Mapping System smoke polygons (OSPO daily KML)",
    modes: ["firewatch"],
    docsUrl: "https://www.ospo.noaa.gov/products/land/hms.html",
    termsUrl: "https://noaa.maps.arcgis.com/home/item.html?id=ab7a5fbd76e3499296350eabf599fc63",
    endpoint: "https://satepsanone.nesdis.noaa.gov/pub/FIRE/web/HMS/Smoke_Polygons/KML/{YYYY}/{MM}/hms_smoke{YYYYMMDD}.kml",
    auth: "None",
    cadence: "Analysts add polygons through the day (1–3 h behind the imagery); the day's file gets a final update the next morning. Worker caches today 20 min, earlier days 3 h",
    caching: "Allowed",
    redistribution: "NOAA states the HMS smoke data are not subject to copyright in the U.S. and waives rights worldwide under CC0 1.0",
    attribution: "NOAA NESDIS Hazard Mapping System",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "noaa-hms",
    verifiedOn: "2026-10-23",
  },
  {
    id: "nws-national",
    name: "NWS active alerts, nationwide (api.weather.gov/alerts/active)",
    modes: ["storm-center"],
    docsUrl: "https://www.weather.gov/documentation/services-web-api",
    termsUrl: "https://www.weather.gov/disclaimer",
    endpoint: "https://api.weather.gov/alerts/active?status=actual",
    auth: "None; identifying User-Agent required (NWS_USER_AGENT)",
    cadence: "Changes minute to minute; Worker caches 3 min (~2 MB in, polygons only out). Counted apart from the home Alerts budget so the Atlas can never use it up",
    caching: "Allowed; NWS asks clients to cache",
    redistribution: "Public domain; don't imply NWS endorsement or present modified text as official",
    attribution: "NOAA/National Weather Service",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "nws-national",
    verifiedOn: "2026-10-23",
  },
  {
    id: "spc-reports",
    name: "NOAA SPC preliminary storm reports (filtered CSV)",
    modes: ["storm-center"],
    docsUrl: "https://www.spc.noaa.gov/climo/reports/",
    termsUrl: "https://www.spc.noaa.gov/faq/",
    endpoint: "https://www.spc.noaa.gov/climo/reports/{YYMMDD}_rpts_filtered.csv (the dated file; the today_/yesterday_ aliases were seen serving empty bodies)",
    auth: "None",
    cadence: "Rewritten every few minutes; report day 1200–1159 UTC. Worker caches today 10 min, yesterday 1 h",
    caching: "Allowed",
    redistribution: "Public domain (SPC FAQ 1.6); reports are preliminary",
    attribution: "NOAA/NWS Storm Prediction Center",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "spc-reports",
    verifiedOn: "2026-10-23",
  },
  {
    id: "nhc-gis",
    name: "NOAA National Hurricane Center active storms + GIS cone/track KMZ",
    modes: ["storm-center"],
    docsUrl: "https://www.nhc.noaa.gov/gis/",
    termsUrl: "https://www.weather.gov/disclaimer",
    endpoint: "https://www.nhc.noaa.gov/CurrentStorms.json; per storm …/storm_graphics/api/{ID}_{ADV}adv_{CONE,TRACK}.kmz",
    auth: "None",
    cadence: "Advisories every 6 h (3 h intermediates); Worker caches 15 min",
    caching: "Allowed",
    redistribution: "Public domain; NHC labels its KML track/cone files experimental",
    attribution: "NOAA/NWS National Hurricane Center",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "nhc-gis",
    verifiedOn: "2026-10-23",
  },
  {
    id: "airnow",
    name: "EPA AirNow API (existing Air quality card; key-based, point queries only)",
    modes: [],
    docsUrl: "https://docs.airnowapi.org/",
    termsUrl: "https://docs.airnowapi.org/faq",
    endpoint: "—",
    auth: "AIRNOW_API_KEY Worker secret (already used by the Air quality card)",
    cadence: "—",
    caching: "—",
    redistribution: "Preliminary data; AirNow asks that it be labelled as such",
    attribution: "U.S. EPA AirNow",
    licenseStatus: "review-required",
    supportsCommercialUse: "unknown",
    built: false,
    verifiedOn: null,
  },
  {
    id: "cdot",
    name: "Colorado DOT traveler data",
    modes: ["roadwatch"],
    docsUrl: "https://www.codot.gov/",
    termsUrl: "https://www.codot.gov/",
    endpoint: "—",
    auth: "Developer registration (not yet requested)",
    cadence: "—",
    caching: "—",
    redistribution: "Unknown — the COtrip website is not a reusable feed",
    attribution: "CDOT",
    licenseStatus: "restricted",
    supportsCommercialUse: "unknown",
    built: false,
    verifiedOn: null,
  },
  {
    id: "noaa-nomads",
    name: "NOAA NOMADS GFS (wind fields)",
    modes: ["windflow"],
    docsUrl: "https://nomads.ncep.noaa.gov/",
    termsUrl: "https://www.weather.gov/disclaimer",
    endpoint: "—",
    auth: "None",
    cadence: "—",
    caching: "—",
    redistribution: "Public domain",
    attribution: "NOAA NCEP",
    licenseStatus: "review-required",
    supportsCommercialUse: true,
    built: false,
    verifiedOn: null,
  },
  {
    id: "celestrak",
    name: "CelesTrak GP elements — ISS only (existing Night sky provider, /api/sky/iss-tle)",
    modes: ["orbit"],
    docsUrl: "https://celestrak.org/NORAD/documentation/gp-data-formats.php",
    termsUrl: "https://celestrak.org/usage-policy.php",
    endpoint: "https://celestrak.org/NORAD/elements/gp.php?CATNR=25544&FORMAT=TLE",
    auth: "None",
    cadence: "GP data updates every 2 h; Worker caches 6 h with a 2 h back-off after any error (CelesTrak's usage policy); positions are propagated in the browser",
    caching: "Allowed by the usage policy (download once per update, stop on any non-200)",
    redistribution: "No license or commercial-use statement published; elements derive from U.S. Space Force tracking — review before commercial distribution",
    attribution: "Orbital elements: CelesTrak",
    licenseStatus: "review-required",
    supportsCommercialUse: "unknown",
    built: true,
    healthKey: "celestrak",
    verifiedOn: "2026-10-23",
  },
  {
    id: "nasa-eonet",
    name: "NASA EONET v3 — open natural events",
    modes: ["planet-events"],
    docsUrl: "https://eonet.gsfc.nasa.gov/docs/v3",
    termsUrl: "https://eonet.gsfc.nasa.gov/what-is-eonet",
    endpoint: "https://eonet.gsfc.nasa.gov/api/v3/events?status=open&category=… (all natural categories) + &category=wildfires&magID=ac&magMin=5000",
    auth: "None",
    cadence: "Curated as sources report (storm fixes every 6 h); Worker caches 30 min",
    caching: "Allowed (API rate limit 60 requests/min; we make two per 30 min)",
    redistribution: "NASA open data (U.S. Government work). EONET is for visualization and general information only, not official extents; linked sources keep their own terms",
    attribution: "NASA EONET",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "nasa-eonet",
    verifiedOn: "2026-10-23",
  },
  {
    id: "usgs-volcanoes",
    name: "USGS Volcano Hazards Program — U.S. volcano alert levels and aviation color codes",
    modes: ["planet-events"],
    docsUrl: "https://volcanoes.usgs.gov/vsc/api/volcanoApi/",
    termsUrl: "https://www.usgs.gov/information-policies-and-instructions/copyrights-and-credits",
    endpoint: "https://volcanoes.usgs.gov/vsc/api/volcanoApi/geojson (the same alert levels HANS publishes, with coordinates)",
    auth: "None",
    cadence: "Changes when an observatory issues a notice; Worker caches 15 min",
    caching: "Allowed; USGS says the API is freely available, supports its own apps and may change without notice",
    redistribution: "Public domain (U.S. Government work)",
    attribution: "USGS Volcano Hazards Program",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "usgs-volcanoes",
    verifiedOn: "2026-10-23",
  },
  {
    id: "noaa-nohrsc",
    name: "NOAA NWS NOHRSC National Snow Analysis (ArcGIS map service)",
    modes: ["mountain-conditions"],
    docsUrl: "https://www.nohrsc.noaa.gov/nsa/",
    termsUrl: "https://www.weather.gov/disclaimer",
    endpoint: "https://mapservices.weather.noaa.gov/raster/rest/services/snow/NOHRSC_Snow_Analysis/MapServer (export tiles; valid time and legend via the Worker)",
    auth: "None",
    cadence: "Daily analysis, service refreshed at 01, 05, 11 and 17 UTC; Worker caches the valid time 1 h, legend 7 days",
    caching: "Tiles go browser → NOAA (CORS allowed); nothing re-hosted",
    redistribution: "Public domain (U.S. Government work)",
    attribution: "NOAA NWS NOHRSC",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: true,
    healthKey: "noaa-nohrsc",
    verifiedOn: "2026-10-23",
  },
  {
    id: "local-adsb",
    name: "Local ADS-B receiver via the Kinmantle local agent",
    modes: ["airspace-live"],
    docsUrl: "https://github.com/wiedehopf/readsb",
    termsUrl: "—",
    endpoint: "LAN only",
    auth: "Local agent pairing",
    cadence: "—",
    caching: "—",
    redistribution: "Household's own receiver",
    attribution: "Local receiver",
    licenseStatus: "verified",
    supportsCommercialUse: true,
    built: false,
    verifiedOn: null,
  },
  {
    id: "gtfs-rt",
    name: "Transit agency GTFS / GTFS-Realtime",
    modes: ["cityflow"],
    docsUrl: "https://gtfs.org/realtime/",
    termsUrl: "Per agency",
    endpoint: "—",
    auth: "Per agency",
    cadence: "—",
    caching: "—",
    redistribution: "Per agency",
    attribution: "Transit agency",
    licenseStatus: "review-required",
    supportsCommercialUse: "unknown",
    built: false,
    verifiedOn: null,
  },
  {
    id: "goes-glm",
    name: "GOES Geostationary Lightning Mapper imagery / local lightning sensor",
    modes: ["lightning-live"],
    docsUrl: "https://www.goes-r.gov/spacesegment/glm.html",
    termsUrl: "—",
    endpoint: "—",
    auth: "—",
    cadence: "—",
    caching: "—",
    redistribution: "Public domain (NOAA) once a tile service is verified",
    attribution: "NOAA",
    licenseStatus: "review-required",
    supportsCommercialUse: "unknown",
    built: false,
    verifiedOn: null,
  },
  {
    id: "windy-webcams",
    name: "Windy Webcams API v3 (free tier)",
    modes: ["webcams"],
    docsUrl: "https://api.windy.com/webcams/api/v3/docs",
    termsUrl: "https://api.windy.com/webcams/terms",
    endpoint: "https://api.windy.com/webcams/api/v3/webcams (nearby / categories, sorted by popularity); /webcams?webcamIds=…&include=images; /map/clusters",
    auth: "WINDY_WEBCAMS_KEY Worker secret (free key from api.windy.com/keys), sent as x-windy-api-key",
    cadence: "Catalog cached 6 h (map cells 12 h); images fetched by id and cached 5 min because their links expire after 10–15 min",
    caching: "Catalog only; image links are never stored past their life. Images at original size or smaller, from the URLs the API returns",
    redistribution: "Free tier: each image must link to its Windy player or page, with a “Webcams provided by Windy.com” credit",
    attribution: "Webcams provided by Windy.com",
    licenseStatus: "review-required",
    supportsCommercialUse: "unknown",
    built: true,
    healthKey: "windy-webcams",
    verifiedOn: "2026-10-26",
  },
  {
    id: "youtube-live",
    name: "Hand-picked 24/7 YouTube live streams (embedded player), checked with YouTube oEmbed",
    modes: ["webcams"],
    docsUrl: "https://developers.google.com/youtube/player_parameters",
    termsUrl: "https://developers.google.com/youtube/terms/api-services-terms-of-service",
    endpoint: "https://www.youtube-nocookie.com/embed/{video}; https://www.youtube.com/oembed?url=…",
    auth: "None",
    cadence: "List in shared/webcams.ts; each stream re-checked for being embeddable every few hours",
    caching: "Only the yes/no embeddable answer",
    redistribution: "Shown only through YouTube’s own embedded player, credited to each channel",
    attribution: "Each channel, on YouTube",
    licenseStatus: "review-required",
    supportsCommercialUse: "unknown",
    built: true,
    healthKey: "youtube-oembed",
    verifiedOn: "2026-10-26",
  },
];
